import { useState, useMemo, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { t } from "i18next";
import CenterLoading from "components/common/CenterLoading";
import PrimaryButton from "components/Form/Buttons/PrimaryButton";
import CustomModal from "components/Modals/CustomModal";
import AICCRuleBuilder from "./aiccRuleBuilder/AICCRuleBuilder";
import { useQueryContext } from "../../context/RuleEndingProvider";
import customFields from "services/apis/customFields";
import rulesApi from "services/apis/rulesApi";
import RuleBuilderConvert from "utils/converter/RuleBuilderConvert";

const RuleCreationTab = ({ overviewId, viewOnly }) => {
  const streamObj = useSelector((state) => state?.datastream?.selected);
  const { query, setQuery } = useQueryContext();
  const [groupData, setGroupData] = useState([]);
  const [ruleQueryString, setRuleQueryString] = useState("");
  const [showQuery, setShowQuery] = useState(false);
  const [confirmQueryReset, setConfirmQueryReset] = useState(false);
  const [configList] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const getRuleById = async () => {
    if (!overviewId) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await rulesApi.getRulesById({
        rulesId: overviewId,
      });

      if (
        response.data?.whereClauses &&
        response.data?.whereClauses.length > 0
      ) {
        setRuleQueryString(response.data?.queryString);

        const respQuery = RuleBuilderConvert.aiccRBWhereClauseToQuery(
          response.data.whereClauses
        );

        setQuery(respQuery);
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  const getFieldConfigJson = async (dataStreamConfigId, schemaId) => {
    if (!dataStreamConfigId) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await customFields.getCustomFieldsByDataStreamConfigId({
        dataStreamConfigId,
        schemaId,
      });

      setGroupData(response.data);
      await getRuleById();
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (streamObj?.dataStreamConfigId) {
      getFieldConfigJson(streamObj?.dataStreamConfigId);
    }
  }, [streamObj?.dataStreamConfigId]);

  const fieldsWithGroup = useMemo(() => {
    return RuleBuilderConvert.aiccRBFieldConfigToGroupedFields(groupData, [
      ...configList.flatMap((e) => e.aiccDataStreamJsons),
      ...configList
        .flatMap((e) => e.apiDetails ?? [])
        .map((e) => ({
          ...e,
          jsonId: e.dataStreamConfigApiId,
          jsonFileSchemaName: e.apiName,
        })),
    ]);
  }, [configList, groupData]);

  const queryChangeCallback = useCallback(
    (myQuery) => {
      setQuery(myQuery);
    },
    [setQuery]
  );
  return (
    <div>
      <div className="rule-creation-header">
        <div className="rule-info">
          <span className="info-icon">
            <i className="fa fa-info-circle"></i>
          </span>
          <div className="info-text">
            <span>
              <strong>{t("aiccRuleBuilder.andCombinatorLabel")}</strong> –{" "}
              {t("aiccRuleBuilder.andCombinatorInfo")}
            </span>
            <span>
              <strong>{t("aiccRuleBuilder.orCombinatorLabel")}</strong> –{" "}
              {t("aiccRuleBuilder.orCombinatorInfo")}
            </span>
          </div>
        </div>
        <div className="d-flex gap-2">
          {ruleQueryString && (
            <PrimaryButton
              title={t("createRuleEngine.ruleCreationTab.viewQueryButton")}
              icon="fa-regular fa-eye"
              onClick={() => {
                setShowQuery(true);
              }}
            />
          )}

          <PrimaryButton
            title={t("CommonKeys.reset")}
            icon="fa-solid fa-arrows-rotate"
            onClick={() => {
              setConfirmQueryReset(true);
            }}
            disabled={
              viewOnly || (query ?? []).filter((e) => !e.isDeleted).length <= 0
            }
          />
        </div>
      </div>

      <CustomModal
        size={"xl"}
        show={showQuery}
        handleClose={() => setShowQuery(false)}
        title={t("createRuleEngine.breadCrumb.title")}
        body={ruleQueryString}
        centered
        okTitle={t("CommonKeys.okButtonTitle")}
        onOk={() => setShowQuery(false)}
      />

      <CustomModal
        show={confirmQueryReset}
        handleClose={() => setConfirmQueryReset(false)}
        title={t("createRuleEngine.breadCrumb.title")}
        body={
          <span className="text-danger fw-bold">
            {t("createRuleEngine.resetRuleBuilder")}
          </span>
        }
        centered
        okTitle={t("CommonKeys.yes")}
        onOk={() => {
          const temp = Array.from(query);

          const finalQuery = temp
            .filter((e) => e.expressionId)
            .map((e) => ({ ...e, isDeleted: true }));

          setQuery(finalQuery);
          setConfirmQueryReset(false);
        }}
        cancelTitle={t("CommonKeys.no")}
        onCancel={() => {
          setConfirmQueryReset(false);
        }}
      />

      {isLoading ? (
        <CenterLoading />
      ) : (
        <div className={`${viewOnly ? "inactive-div" : ""}`}>
          <AICCRuleBuilder
            query={query}
            onChangeQuery={queryChangeCallback}
            useFieldGroup
            allowValueSource
            allowArithmetic
            fieldsWithGroup={fieldsWithGroup}
          />
        </div>
      )}
    </div>
  );
};

export default RuleCreationTab;
